import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import {throwError as observableThrowError, lastValueFrom, Subject, Observable } from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApiHelperService } from './api-helper.service';
import { GenericAPIArrayResponse, GenericAPIResponse, LoginApiResponse } from '../models/api-response';
import { PlaybookViewOverview, PlaybookCoverage, PlaybookFolder, PlaybookFolderGroup, PlaybookGraphicData, PlaybookInstallShare, PlaybookInstallView, PlaybookPlay, PlaybookPlayType, PlaybookRoute, PlaybookScript, PlaybookTemplate, PlaybookTemplateCategory, PlaybookTextbox, PlaybookType, PlaybookScriptView, PlaybookViewTimeEvent, PlaybookViewTimeResult } from '../models/playbook';

@Injectable({
  providedIn: 'root'
})
export class PlaybookService {


  private refreshFolder = new Subject<any>();
  private expandToFolder = new Subject<string>();
  private folderUpdated = new Subject<{folderid: string, type: PlaybookPlayType}>();
  private folderDeleted = new Subject<{folderid: string, type: PlaybookPlayType}>();
  private refreshPlayCount = new Subject<{type: PlaybookPlayType, playids: string[]}>();
  private refreshSharedInstallsCount = new Subject();

  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelperService,
    private router: Router
  ) { }


  listenForFolderRefresh(): Observable<any> {
    return this.refreshFolder.asObservable();
  }

  emitFolderRefresh() {
    this.refreshFolder.next(null);
  }

  listenForExpandToFolder(): Observable<string> {
    return this.expandToFolder.asObservable();
  }

  emitExpandToFolder(folderid: string) {
    this.expandToFolder.next(folderid);
  }

  listenForFolderUpdated(): Observable<{folderid: string, type: PlaybookPlayType}> {
    return this.folderUpdated.asObservable();
  }

  emitFolderUpdated(folderid: string, type: PlaybookPlayType) {
    this.folderUpdated.next({folderid, type});
  }

  listenForFolderDeleted(): Observable<{folderid: string, type: PlaybookPlayType}> {
    return this.folderDeleted.asObservable();
  }

  emitFolderDeleted(folderid: string, type: PlaybookPlayType) {
    this.folderDeleted.next({folderid, type});
  }

  listenForRefreshPlayCount(): Observable<{type: PlaybookPlayType, playids: string[]}> {
    return this.refreshPlayCount.asObservable();
  }

  emitRefreshPlayCount(type: PlaybookPlayType, playids: string[] = []) {
    this.refreshPlayCount.next({type, playids});
  }

  listenForRefreshSharedInstallsCount(): Observable<any> {
    return this.refreshSharedInstallsCount.asObservable();
  }

  emitRefreshSharedInstallsCount() {
    this.refreshSharedInstallsCount.next(null);
  }

  getPlaybookPlay(_id: string): Promise<GenericAPIResponse<PlaybookPlay>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIResponse<PlaybookPlay>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPlaybookPlays(): Promise<GenericAPIArrayResponse<PlaybookPlay>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookPlay>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getDeletedPlaybookPlays(): Promise<GenericAPIArrayResponse<PlaybookPlay>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays/deleted`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookPlay>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  restoreDeletedPlays(playids: string[]): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays/restore`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {playids};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  permanentlyDeletePlays(playids: string[]): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays/permanently-delete`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {playids};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  queryPlaybookPlays(teamid: string, queryObj?: any): Promise<GenericAPIArrayResponse<PlaybookPlay>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, queryObj};

    return lastValueFrom(this.http.put<GenericAPIArrayResponse<PlaybookPlay>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPlaybookPlayValues(teamid: string, valueName: string, queryObj?: any): Promise<GenericAPIArrayResponse<string>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays/values`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, valueName, queryObj};

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<string>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }


  searchPlaybookPlays(teamid: string, searchText: string, type?: PlaybookPlayType, additionalQuery?: any): Promise<GenericAPIArrayResponse<PlaybookPlay>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays/search`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, type, searchText, additionalQuery};

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<PlaybookPlay>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPlaybookPlayCount(teamid: string, queryObj?: any): Promise<GenericAPIResponse<number>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays/count`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, queryObj};

    return lastValueFrom(this.http.post<GenericAPIResponse<number>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  postPlaybookPlay(body: Partial<PlaybookPlay>): Promise<GenericAPIResponse<PlaybookPlay>> { //this updates or creates the play, depending on if an _id is included
    const url = `${this.apiHelper.apiUrl()}/playbook/plays`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookPlay>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deletePlaybookPlay(_id: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/plays/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createPlaybookFolder(teamid: string, name: string, type: PlaybookPlayType, playids: string[], date?: Date, seasonid?: number): Promise<GenericAPIResponse<PlaybookFolder>> {
    const body = {
      teamid,
      name,
      type,
      playids,
			date,
      seasonid
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/folders`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookFolder>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updatePlaybookFolder(_id: string, update: Partial<PlaybookFolder>): Promise<GenericAPIResponse<PlaybookFolder>> {
    const body = {
      _id,
      update
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/folders`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookFolder>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPlaybookFolders(): Promise<GenericAPIArrayResponse<PlaybookFolder>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/folders`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookFolder>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPlaybookFolder(folderid: string, external: boolean = false): Promise<GenericAPIResponse<PlaybookFolder>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/folder${external ? '-external' : ''}/${folderid}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIResponse<PlaybookFolder>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getSharedPlaybookFolders(): Promise<GenericAPIArrayResponse<PlaybookFolder>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/shared-installs`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookFolder>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getFolderStructure(playType: PlaybookPlayType): Promise<GenericAPIArrayResponse<PlaybookFolder>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/folders/${playType}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookFolder>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deletePlaybookFolder(_id: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/folders/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  addPlaysToPlaybookFolder(_id: string, playids: string[]): Promise<GenericAPIResponse<PlaybookFolder>> {
    const body = {
      playids
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/folders/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookFolder>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  removePlaysFromPlaybookFolder(_id: string, playids: string[]): Promise<GenericAPIResponse<PlaybookFolder>> {
    const body = {
      playids
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/folders/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookFolder>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createPlaybookFolderGroup(teamid: string, type: PlaybookPlayType, parentfolderid: string, name: string): Promise<GenericAPIResponse<PlaybookFolderGroup>> {
    const body = {
      teamid,
      type,
      parentfolderid,
      name
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/groups`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookFolderGroup>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updatePlaybookFolderGroup(_id: string, update: Partial<PlaybookFolderGroup>): Promise<GenericAPIResponse<PlaybookFolderGroup>> {
    const body = {update};
    const url = `${this.apiHelper.apiUrl()}/playbook/groups/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookFolderGroup>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deletePlaybookFolderGroup(_id: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/groups/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  submitPlaybookInstallView(installid: string, playid: string): Promise<GenericAPIResponse<PlaybookInstallView>> {
    const body = {
      installid,
      playid
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/install-views`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookInstallView>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPlaybookInstallView(installid: string): Promise<GenericAPIResponse<PlaybookInstallView>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/install-views/${installid}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIResponse<PlaybookInstallView>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updatePlaybookInstallView(installid: string, update: Partial<PlaybookInstallView>): Promise<GenericAPIResponse<PlaybookInstallView>> {
    const body = {
      installid,
      update
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/install-views`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookInstallView>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  postPlaybookViewTimeEvents(events: PlaybookViewTimeEvent[]): Promise<GenericAPIResponse<PlaybookViewTimeResult>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/view-time-events`;
    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookViewTimeResult>>(url, {events}, {headers: this.apiHelper.apiHeaderWithToken()}));
  }

  getInstallViewProgress(installid: string): Promise<GenericAPIArrayResponse<PlaybookViewOverview>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/install-view-progress/${installid}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookViewOverview>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  submitPlaybookScriptView(scriptid: string, playid: string): Promise<GenericAPIResponse<PlaybookScriptView>> {
    const body = {
      scriptid,
      playid
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/script-views`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookScriptView>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPlaybookScriptView(scriptid: string): Promise<GenericAPIResponse<PlaybookScriptView>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/script-views/${scriptid}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIResponse<PlaybookScriptView>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updatePlaybookScriptView(scriptid: string, update: Partial<PlaybookScriptView>): Promise<GenericAPIResponse<PlaybookScriptView>> {
    const body = {
      scriptid,
      update
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/script-views`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookScriptView>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getScriptViewProgress(scriptid: string): Promise<GenericAPIArrayResponse<PlaybookViewOverview>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/script-view-progress/${scriptid}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookViewOverview>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  copyPlaybookPlays(teamid: string, playids: string[], folderid?: string): Promise<GenericAPIResponse<undefined>> {
    const body = {
      teamid,
      playids,
      folderid
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/copy-plays`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getScripts(seasonid?: number): Promise<GenericAPIArrayResponse<PlaybookScript>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/scripts${seasonid ? `/${seasonid}` : ''}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookScript>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getScript(scriptid?: string): Promise<GenericAPIResponse<PlaybookScript>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/script/${scriptid}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIResponse<PlaybookScript>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  postScript(script: Partial<PlaybookScript>): Promise<GenericAPIResponse<PlaybookScript>> { //this updates or creates the script, depending on if an _id is included
    const body = {
      script
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/scripts`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookScript>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  duplicateScript(sourceScriptId: string): Promise<GenericAPIResponse<PlaybookScript>> { //this updates or creates the script, depending on if an _id is included
    const body = {
      sourceScriptId
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/scripts`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookScript>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteScript(_id: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/scripts/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getSharedPlaybookScripts(): Promise<GenericAPIArrayResponse<PlaybookScript>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/shared-scripts`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookScript>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  addScriptPlay(scriptid: string, play: PlaybookPlay): Promise<GenericAPIResponse<PlaybookScript>> {
    const body = {
      scriptid,
      play
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/scripts/play`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookScript>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  addPlaysToScript(scriptid: string, plays: PlaybookPlay[]): Promise<GenericAPIResponse<PlaybookScript>> {
    const body = {
      scriptid,
      plays
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/scripts/plays`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookScript>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateScriptPlay(scriptid: string, playid: string, update: Partial<PlaybookPlay>): Promise<GenericAPIResponse<PlaybookScript>> {
    const body = {
      scriptid,
      playid,
      update
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/scripts/play`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookScript>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteScriptPlay(scriptid: string, playid: string): Promise<GenericAPIResponse<PlaybookScript>> {
    const body = {
      scriptid,
      playid
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/scripts/delete-play`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookScript>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  exportPlaybookPlays(teamid: string, parentfolderid: string, seasonid: number, name: string, playids: string[]): Promise<GenericAPIResponse<undefined>> {
    const body = {
			teamid,
			parentfolderid,
			seasonid,
			name,
			playids
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/export`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  sendPlaybookShareNotifications(teamid: string, userids: string[], numPlays: number): Promise<GenericAPIResponse<undefined>> {
    const body = {
			teamid,
      userids,
      numPlays
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/notifications`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  shareInstallWithTeam(destTeamid: string, installid: string): Promise<GenericAPIResponse<PlaybookInstallShare>> {
    const body = {
      destTeamid,
      installid
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/external-shares`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookInstallShare>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getTeamSharedInstalls(): Promise<GenericAPIArrayResponse<PlaybookInstallShare>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/external-shares`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookInstallShare>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getNewSharedInstallsCount(): Promise<GenericAPIResponse<number>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/external-shares-new-count`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIResponse<number>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getTeamInstallSentHistory(): Promise<GenericAPIArrayResponse<PlaybookInstallShare>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/external-share-history`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookInstallShare>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  copySharedInstallPlays(playbookInstallShareId: string, playids: string[], type: PlaybookPlayType, installid?: string): Promise<GenericAPIArrayResponse<PlaybookPlay>> {
    const body = {
      playbookInstallShareId,
      playids,
      type,
      installid
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/external-shares`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIArrayResponse<PlaybookPlay>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateInstallShare(_id: string, update: Partial<PlaybookInstallShare>): Promise<GenericAPIResponse<PlaybookInstallShare>> {
    const body = {
      update
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/external-shares/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookInstallShare>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteInstallShare(shareid: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/external-shares/${shareid}`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  sendExternalInstallShareEmail(installid: string, link: string, emails: string[]): Promise<GenericAPIResponse<PlaybookInstallShare>> {
    const body = {
      installid,
      link,
      emails
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/send-external-install-share-email`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookInstallShare>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPlaybookTemplateCategories(): Promise<GenericAPIArrayResponse<PlaybookTemplateCategory>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/template-categories`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookTemplateCategory>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createPlaybookTemplateCategory(name: string): Promise<GenericAPIResponse<PlaybookTemplateCategory>> {
    const body = {
      name
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/template-categories`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookTemplateCategory>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updatePlaybookTemplateCategory(_id: string, update: Partial<PlaybookTemplateCategory>): Promise<GenericAPIResponse<PlaybookTemplateCategory>> {
    const body = {
      update
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/template-categories/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookTemplateCategory>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deletePlaybookTemplateCategory(_id: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/template-categories/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPlaybookTemplates(): Promise<GenericAPIArrayResponse<PlaybookTemplateCategory>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/templates`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<PlaybookTemplateCategory>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  searchPlaybookTemplates(searchText: string): Promise<GenericAPIArrayResponse<PlaybookTemplateCategory>> {
    const body = {searchText};
    const url = `${this.apiHelper.apiUrl()}/playbook/templates/search`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<PlaybookTemplateCategory>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createPlaybookTemplate(categoryid: string, name: string, losY: number, drawing: PlaybookGraphicData[] = [], coverages: PlaybookCoverage[] = [], routes: PlaybookRoute[] = [], textboxes: PlaybookTextbox[] = []): Promise<GenericAPIResponse<PlaybookTemplate>> {
    const body = {
      categoryid,
      name,
      losY,
      drawing,
      coverages,
      routes,
      textboxes
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/templates`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<PlaybookTemplate>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updatePlaybookTemplate(_id: string, update: Partial<PlaybookTemplate>): Promise<GenericAPIResponse<PlaybookTemplate>> {
    const body = {
      update
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/templates/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<PlaybookTemplate>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deletePlaybookTemplate(_id: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/playbook/templates/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  generatePdfFromPlays(plays: PlaybookPlay[], numPerPage: number = 1, landscape: boolean = false, scriptid?: string): Promise<GenericAPIResponse<string>> {
    const body = {
      plays,
      numPerPage,
      landscape,
      scriptid,
      mobile: false
    };
    const url = `${this.apiHelper.apiUrl()}/playbook/pdf`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<string>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }
}
